import { colors, fonts, radii } from '@steelwake/ui';
import { useEffect, useRef, type RefObject } from 'react';

import type { RenderStatsSource } from '../game/render/host.js';

const REFRESH_MS = 250;
const DRAW_CALL_BUDGET = 400;

type Field =
  | 'fps'
  | 'frame'
  | 'cpu'
  | 'gpu'
  | 'scale'
  | 'buffer'
  | 'backend'
  | 'drawcalls'
  | 'triangles'
  | 'preset';

function fmt(v: number, digits = 1): string {
  return Number.isFinite(v) ? v.toFixed(digits) : '–';
}

/**
 * Frame-time overlay (BUILD_PLAN Phase 2). Writes straight into the DOM four times a second
 * instead of updating React state per frame, so it never causes a render storm.
 */
export function FrameOverlay({ gameRef }: { gameRef: RefObject<RenderStatsSource | null> }) {
  const fields = useRef<Partial<Record<Field, HTMLSpanElement | null>>>({});
  const bind = (name: Field) => (el: HTMLSpanElement | null) => {
    fields.current[name] = el;
  };

  useEffect(() => {
    const set = (name: Field, text: string, color?: string) => {
      const el = fields.current[name];
      if (!el) return;
      if (el.textContent !== text) el.textContent = text;
      if (color !== undefined) el.style.color = color;
    };
    const id = window.setInterval(() => {
      const snap = gameRef.current?.snapshot();
      if (!snap || snap.frames === 0) return;
      set('fps', fmt(snap.fps));
      set('frame', `${fmt(snap.frame.avg)} / ${fmt(snap.frame.p95)}`);
      set('cpu', `${fmt(snap.cpu.avg)} / ${fmt(snap.cpu.p95)}`);
      set('gpu', snap.gpu ? `${fmt(snap.gpu.avg)} / ${fmt(snap.gpu.p95)}` : 'n/a');
      set(
        'scale',
        `${fmt(snap.renderScale.current, 2)}${snap.signal === 'gpu' ? '' : ' (cpu-timed)'}`,
      );
      set('buffer', `${String(snap.drawingBuffer.width)}×${String(snap.drawingBuffer.height)}`);
      set('backend', snap.backend ?? '–');
      set(
        'drawcalls',
        String(snap.drawCalls),
        snap.drawCalls > DRAW_CALL_BUDGET ? colors.danger : colors.text,
      );
      set('triangles', `${(snap.triangles / 1000).toFixed(1)}k`);
      set('preset', snap.preset);
    }, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [gameRef]);

  return (
    <div
      data-testid="frame-overlay"
      aria-live="off"
      style={{
        position: 'absolute',
        top: 12,
        left: 12,
        padding: '8px 10px',
        background: 'rgba(11, 13, 16, 0.72)',
        border: `1px solid ${colors.border}`,
        borderRadius: radii.sm,
        color: colors.text,
        fontFamily: fonts.mono,
        fontSize: 12,
        lineHeight: 1.6,
        whiteSpace: 'pre',
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      <span data-testid="stats-fps" ref={bind('fps')}>
        –
      </span>
      {' fps · frame '}
      <span data-testid="stats-frame" ref={bind('frame')}>
        –
      </span>
      {' ms (avg / p95)\n'}
      {'cpu '}
      <span data-testid="stats-cpu" ref={bind('cpu')}>
        –
      </span>
      {' · gpu '}
      <span data-testid="stats-gpu" ref={bind('gpu')}>
        –
      </span>
      {' ms\n'}
      {'scale '}
      <span data-testid="stats-scale" ref={bind('scale')}>
        –
      </span>
      {' · '}
      <span data-testid="stats-buffer" ref={bind('buffer')}>
        –
      </span>
      {' · '}
      <span data-testid="stats-backend" ref={bind('backend')}>
        –
      </span>
      {'\ndraw '}
      <span data-testid="stats-drawcalls" ref={bind('drawcalls')}>
        –
      </span>
      {' · tris '}
      <span data-testid="stats-triangles" ref={bind('triangles')}>
        –
      </span>
      {' · '}
      <span data-testid="stats-preset" ref={bind('preset')}>
        –
      </span>
    </div>
  );
}
